import { Component, createRef, type ReactNode } from 'react';
import type { ControlAnimation } from '../types';

/**
 * Animates wizard screen changes (intro -> step N -> results), per the
 * calculator's Animation setting (Brad, 2026-09-28):
 *   none   instant swap
 *   fade   old screen fades out, then the new one fades in
 *   slide  forward: old slides out left, new slides in from the right;
 *          back: the reverse
 *
 * The outgoing screen has already been replaced in React's tree by the time
 * the new one renders, so it's animated from a snapshot: a static DOM clone
 * taken in getSnapshotBeforeUpdate (i.e. before the swap), overlaid on the
 * stage, made inert, and removed when its exit animation ends. The incoming
 * screen is re-keyed so it mounts fresh and plays its enter animation.
 */

export const SCREEN_TRANSITION_MS = 320;

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

type Direction = 'forward' | 'back';

interface Props {
  /** Identity of the current screen; a change triggers a transition. */
  screenKey: string;
  /** Position in the flow; higher = further along. Decides the direction. */
  order: number;
  animation: ControlAnimation;
  children: ReactNode;
}

interface State {
  key: string;
  order: number;
  /** Direction of the most recent change; null on first mount (no animation). */
  dir: Direction | null;
}

export class ScreenTransition extends Component<Props, State> {
  private stageRef = createRef<HTMLDivElement>();
  private timers: number[] = [];

  state: State = { key: this.props.screenKey, order: this.props.order, dir: null };

  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    if (props.screenKey === state.key) return null;
    return { key: props.screenKey, order: props.order, dir: props.order >= state.order ? 'forward' : 'back' };
  }

  private get animated(): boolean {
    return this.props.animation !== 'none' && !prefersReducedMotion();
  }

  getSnapshotBeforeUpdate(prevProps: Props): HTMLElement | null {
    if (prevProps.screenKey === this.props.screenKey || !this.animated) return null;
    const current = this.stageRef.current?.querySelector<HTMLElement>(':scope > .hsab-calc__screen');
    if (!current) return null;

    const clone = current.cloneNode(true) as HTMLElement;
    // cloneNode copies attributes, not live form state: carry over what the
    // user sees so the exiting screen doesn't flash reset values.
    const from = current.querySelectorAll<HTMLInputElement>('input, select, textarea');
    const to = clone.querySelectorAll<HTMLInputElement>('input, select, textarea');
    from.forEach((el, i) => {
      const c = to[i];
      if (!c) return;
      if (el.type === 'checkbox' || el.type === 'radio') c.checked = el.checked;
      else c.value = el.value;
      c.removeAttribute('id');
      c.removeAttribute('name');
    });
    clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('inert', '');
    clone.classList.add('hsab-calc__screen--leaving');
    clone.removeAttribute('data-enter');
    return clone;
  }

  componentDidUpdate(_prev: Props, _prevState: State, clone: HTMLElement | null) {
    const stage = this.stageRef.current;
    if (!clone || !stage) return;
    clone.setAttribute('data-leave', this.state.dir ?? 'forward');
    stage.appendChild(clone);
    stage.classList.add('hsab-calc__screen-stage--animating');
    this.timers.push(
      window.setTimeout(() => {
        clone.remove();
        if (!stage.querySelector('.hsab-calc__screen--leaving')) {
          stage.classList.remove('hsab-calc__screen-stage--animating');
        }
      }, SCREEN_TRANSITION_MS),
    );
  }

  componentWillUnmount() {
    this.timers.forEach((t) => window.clearTimeout(t));
  }

  render() {
    const enter = this.animated && this.state.dir ? this.state.dir : undefined;
    return (
      <div className="hsab-calc__screen-stage" ref={this.stageRef}>
        <div className="hsab-calc__screen" key={this.state.key} data-enter={enter}>
          {this.props.children}
        </div>
      </div>
    );
  }
}
